"use client";
import React, { useRef, MouseEventHandler, useCallback } from "react";
import styled from "styled-components";

import Box, { BoxProps } from "../Box";
import { AnyObject } from "../../models";
import TextEditor from "../TextEditor";
import { SaveDataBuilders } from "../TextEditor/TextEditor";
import { useThemeColor } from "../TextEditor/hooks/useThemeColor";

export const noteTestId = "gallery-note-text";
export const editorTestId = "gallery-note-editor";
export const wrapperTestId = "gallery-note-wrapper";

type NoteItem = {
  id: string | number;
  data: {
    content?: string;
    backgroundColor?: string;
  };
  type: string;
};

type Props<Item extends NoteItem> = {
  item: Item;
  selected?: boolean;
  canEdit?: boolean;
  isEditing?: boolean;
  hasDefaultValue?: boolean;
  onBeginEditing?: (id: Item["id"]) => void;
  onFinishEditing?: (item: Item, data: AnyObject) => void;
  onClick?: MouseEventHandler;
  backgroundColorMenu?: React.ReactNode;
} & BoxProps;

type WrapperProps = { canEdit: boolean; isEditing: boolean } & BoxProps;

const Wrapper = styled(Box).attrs<WrapperProps>(() => {
  return {
    px: "s",
    flex: "1 1 100%",
  };
})<WrapperProps>`
  transition: min-height 0.4s ease;

  ${props =>
    props.canEdit &&
    !props.isEditing &&
    `
    cursor: pointer;
  `}
`;

const GalleryNote = <Item extends NoteItem>({
  item,
  selected,
  canEdit,
  isEditing,
  hasDefaultValue,
  onBeginEditing,
  onFinishEditing,
  onClick,
  backgroundColorMenu,
  ...rest
}: Props<Item>) => {
  const borderColor = useThemeColor("primary");
  const noteRef = useRef<HTMLDivElement>();
  const noteProps = {
    canEdit,
    borderRadius: "default",
    border: selected && canEdit ? `2px solid ${borderColor}` : "2px solid transparent",
  };

  const handleFinishEditing = useCallback(
    (data: AnyObject | null) => {
      onFinishEditing(item, data);
    },
    [item, onFinishEditing]
  );

  return (
    <Wrapper data-testid={wrapperTestId} canEdit={canEdit} isEditing={isEditing} {...rest}>
      <Box {...noteProps} ref={noteRef} data-testid={noteTestId}>
        <TextEditor
          itemId={item.id}
          content={item.data?.content}
          buildSaveData={SaveDataBuilders.noteContent}
          selected={selected}
          canEdit={canEdit}
          isEditing={isEditing}
          hasDefaultValue={hasDefaultValue}
          onBeginEditing={onBeginEditing}
          onFinishEditing={handleFinishEditing}
          backgroundColor={item.data?.backgroundColor}
          backgroundColorMenu={backgroundColorMenu}
          onClick={canEdit ? onClick : null}
        />
      </Box>
    </Wrapper>
  );
};

export default GalleryNote;
